import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { TabsBar } from '@/common/TabsBar';
import { useHasStudentsTab } from '~/hooks/useIsCoordinatorGroup';
import { useGroupAdminAccess } from '~/hooks/useIsGroupAdmin';
import { useStudentTabs } from '~/hooks/useStudentTabs';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students',
)({
  component: StudentsLayout,
});

/**
 * The guard for the whole Students tab — all five sub-trees under it, not just
 * All Students.
 *
 * Legacy's Students tab is one `WBS_Group_Extension` per group type, and
 * `matchRequirements` (`WBS_Group_Extension.php:144-166`) gates it on two axes:
 *
 * - *group type*: `matchGroupTypes()` is its first statement, and the four
 *   extensions' `groupTypes` name no social group and match no untyped group.
 * - *membership*: all four declare `$this->groupMemberships = 'admin'`, and
 *   `WBS_Group_Types::inMembership` maps `'admin'` to `groups_is_user_admin`
 *   alone (`WBS_Group_Types.php:32-43`) — a mod is denied and so is a plain
 *   member. Measured live: uid 855, a mod of congregation 404, gets
 *   `Page not found` on `/students/all-students/`.
 *
 * A refused caller gets `Page not found` on *every* subnav item, because those
 * items live under the gate that already returned false — hence one layout
 * guard rather than a per-leaf one. It mirrors `StudentsTabCheck` on
 * `GET /v3/groups/{group_id}/students`, so a denial is a redirect instead of
 * four painted 403s.
 *
 * `isResolved` is not optional: `getGroupBySlug` and `/users/me` are both
 * pending on first paint, and a bare `!allowed` bounces every caller — the
 * group's own admin included — before the payload lands.
 */
function StudentsLayout(): ReactElement | null {
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const studentTabs = useStudentTabs();
  const { isGroupAdmin, isResolved } = useGroupAdminAccess();
  const hasStudentsTab = useHasStudentsTab();
  const refused = isResolved && !(hasStudentsTab && isGroupAdmin);

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <TabsBar tabs={studentTabs} base="/groups/$groupName/students" />
      <Outlet />
    </>
  );
}
